import React, { useEffect, useState } from "react";
import "./SkuConsumptionGraph.scss";
import { Chart } from "chart.js/auto";
import Filter from "../Filter/Filter";
import { FilterOptionSchema } from "../Filter/filter.data";
import ApiService from "../../services/apiService";
import URLS from "../../urls/Urls";
import { useSelector } from "react-redux";
import { userDetails } from "../../redux/user/userSelector";

interface ChartCityData {
  city: string;
  value: number[];
}

const SkuConsumptionGraph: React.FC = () => {
  const [skusList, setSkusList] = useState<FilterOptionSchema[]>([]);
  const [selectedSku, setSelectedSku] = useState<FilterOptionSchema[]>([]);

  const [skuChartData, setSkuChartData] = useState<{
    [key: number]: { city: string; value: number[] }[];
  }>({});

  const useDetail = useSelector(userDetails);

  useEffect(() => {
    fetchSkus(useDetail.company_id || -1);
  }, []);

  useEffect(() => {
    if (selectedSku.length > 0) {
      const selectedId = selectedSku[0].id;
      if (skuChartData[selectedId]) {
        renderSkuChart(skuChartData[selectedId]);
      }
    }
  }, [selectedSku, skuChartData]);

  const fetchSkus = async (client_id: number) => {
    try {
      const { data } = await ApiService().client.get(
        URLS.DASHBOARD.SKU_CONSUMPTION_DETAILS(client_id),
      );

      const skusRaw = data?.data || {};
      const filterOptions: FilterOptionSchema[] = Object.values(skusRaw).map(
        (sku: any) => ({
          id: sku.id,
          name: sku.name,
        }),
      );

      setSkusList(filterOptions);
      if (filterOptions.length > 0) {
        setSelectedSku([filterOptions[0]]);
      }
      const chartData: { [key: number]: ChartCityData[] } = {};
      Object.values(skusRaw).forEach((sku: any) => {
        const cityData: ChartCityData[] = Object.values(sku.value).map(
          (city: any) => ({
            city: city.city,
            value: city.value,
          }),
        );
        chartData[sku.id] = cityData;
      });
      setSkuChartData(chartData);

      setSkuChartData(chartData);
    } catch (e) {
      console.error(e);
    }
  };

  const clearSkuChart = async () => {
    const chartStatus = Chart.getChart("sku-consumption-report-graph");
    if (chartStatus !== undefined) {
      chartStatus.destroy();
    }
  };

  const renderSkuChart = async (cityData: ChartCityData[]) => {
    await clearSkuChart();

    const canvas = document.getElementById(
      "sku-consumption-report-graph",
    ) as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.setAttribute("height", `${10 * 19 + 26}px`);

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const currentMonth = new Date().getMonth();
    const labels = months.slice(3, currentMonth + 1);

    const colorPalette = [
      "#4D4DFF",
      "#FFC107",
      "#FF5722",
      "#009688",
      "#9C27B0",
      "#3F51B5",
      "#00BCD4",
      "#E91E63",
    ];

    const allValues = cityData.flatMap((city) => city.value);
    const maxValue = Math.max(...allValues);
    const paddedMax = Math.ceil(maxValue * 1.1);
    const diff = maxValue < 500 ? 10 : 100;
    const stepSize = Math.ceil(paddedMax / 5 / diff) * diff;
    const suggestedMax = stepSize * 5;

    new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: cityData.map((city, index) => ({
          label: city.city,
          data: city.value,
          backgroundColor: colorPalette[index % colorPalette.length],
          barThickness: 12,
          borderRadius: 4,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              boxWidth: 12,
              font: {
                size: 12,
                family: "Inter",
                weight: 500,
              },
              color: "#333",
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax,
            ticks: {
              stepSize,
              padding: 12,
              color: "#33333399",
              callback: function (tickValue: string | number) {
                if (typeof tickValue === "number") {
                  return tickValue.toLocaleString("en-IN");
                }
                return tickValue;
              },
              font: {
                size: 12,
                weight: 500,
                family: "Inter",
              },
            },
            grid: {
              display: true,
              color: "#E5E7EB",
            },
            border: {
              display: false,
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: "#333333",
              font: {
                size: 12,
                weight: 500,
                family: "Inter",
              },
            },
          },
        },
      },
    });
  };

  return (
    <div className="sku-consumption-main-chart-container py-14 px-16 flex-column gap-18 flex-1">
      <div className="flex-row align-items-center justify-content-space-between">
        <p
          style={{ color: "#333333CC" }}
          className="xetgo-font-tag bold flex-1"
        >
          SKU Consumption
        </p>
        <div>
          <Filter
            option={{
              key: "skuName",
              name: "Sku",
            }}
            optionList={skusList}
            multipleSelection={false}
            onSelection={(val, _option) => {
              if (val) {
                setSelectedSku(val);
              } else {
                setSelectedSku([]);
              }
            }}
            mode="showNames"
            showPlaceholder={true}
            direction="right"
            addNew={false}
            classname="report-sku-filter"
            preSelected={selectedSku}
          />
        </div>
      </div>
      <div className="chart-wrapper">
        <canvas id="sku-consumption-report-graph"></canvas>
      </div>
    </div>
  );
};

export default SkuConsumptionGraph;
